import { UtilHelper } from '@/util/helper';
import moment from 'moment';
import { HelpCenterVersionsEntry } from '@/help-center/types/help-center-version-entry';
import { ContentfulHelper } from '@/contentful/helper';

/**
 * Replaces all \n characters with <br> tags
 *
 * @remarks
 * If first character of the string is \n
 * then it is removed and not replaced with a <br> tag
 *
 * @param str - string to parse
 *
 * @returns parsed string with <br> tags
 */
export const parseNewLines = (str: string): string => {
	let formattedString = str;
	const isFirstCharNewLine = formattedString[0] === '\n';

	if (isFirstCharNewLine) {
		formattedString = formattedString.slice(1);
	}

	formattedString = formattedString.replace(/\n/g, '<br>');

	return formattedString;
};

/**
 * Formats a HelpCenter FAQ Answer string by parsing images, \n, and underscores
 *
 * @param str - string to format
 *
 * @returns formatted string
 */
export const formatFAQAnswer = (answer: string): string => {
	let formattedAnswer = answer;

	formattedAnswer = ContentfulHelper.parseImages(formattedAnswer);
	formattedAnswer = parseNewLines(formattedAnswer);
	formattedAnswer = UtilHelper.parseHTMLTags({
		str: formattedAnswer,
		tag: 'strong',
		toParse: '__',
	});

	return formattedAnswer;
};

/**
 * Formats a HelpCenter Version Description
 *
 * @remarks
 * This code is inherently unique to the requirements for formatting this specific type of string
 *
 * @param str - string to format
 *
 * @returns formatted string
 */
export const formatVersionDescription = (str: string): string => {
	let formattedString = str;
	/* adding eslint ignore rule per CYBER-6706 */
	/* eslint-disable */
	const subTitleRegex = /.+?(?<=:)/;
	const endStringRegex = /(?<=---\n\n).*/;
	/* eslint-enable */
	const [subtitle] = formattedString.match(subTitleRegex) || [null];
	const [endString] = formattedString.match(endStringRegex) || [null];

	if (subtitle) {
		formattedString = formattedString.replace(subTitleRegex, `<strong>${subtitle}</strong><ul class="ml-4 mb-4">`);
	}

	if (endString) {
		formattedString = formattedString.replace(endStringRegex, `<strong>${endString}</strong>`);
	}

	formattedString = formattedString.replace(/---/g, '</li></ul><hr class="light">');
	formattedString = formattedString.replace(/\n-/g, '<li>');
	formattedString = formattedString.replace(/\n\n/g, '\n');
	formattedString = formattedString.replace(/\n/g, '<br>');

	return formattedString;
};

/**
 * Returns the full released on string for a versions release date
 *
 * @param entry - the version Entry
 *
 * @returns formatted released on string
 */
export const getVersionEntryReleasedOn = (entry: HelpCenterVersionsEntry): string => {
	const date = entry?.fields.date;
	const formattedDate = moment(date).format('MMM Do YYYY');

	return `Released on ${formattedDate}`;
};

/**
 * Returns the full string for a Version title
 *
 * @param entry - the version Entry
 *
 * @returns the formatted title
 */
export const getVersionEntryTitle = (entry: HelpCenterVersionsEntry): string =>
	`What's New In ${entry?.fields.versionNumber || ''}`;
